import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, Eye, Star } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { toast } from '../hooks/use-toast';
import { isAuthenticated } from '../lib/authUtils';
import { useVehicle } from '../contexts/VehicleContext';
import { fitsVehicle } from '../lib/plate';


const ProductCard = React.memo(({ product }) => {
  const { addToCart, addToWishlist, isInWishlist, addToCompare } = useCart();
  const navigate = useNavigate();
  const { vehicle } = useVehicle();
  const fits = fitsVehicle(product, vehicle);
  // the first few specifications defined by the product's category attribute set (dictionary order)
  const chips = ((product.raw && product.raw.attributeSpecs) || []).filter((s) => !['part_type', 'terminal_layout', 'capacity_ah', 'start_stop'].includes(s.key)).slice(0, 3);

  const handleAddToCart = (e) => {
    e.preventDefault();
    
    // Check if user is authenticated
    if (!isAuthenticated()) {
      toast({
        title: 'Login Required',
        description: 'Please login to add items to your cart.',
        variant: 'destructive',
      });
      navigate('/customer-login', { state: { from: { pathname: window.location.pathname } } });
      return;
    }

    addToCart(product);
    toast({
      title: 'Added to cart',
      description: `${product.name} has been added to your cart.`
    });
  };

  const handleAddToWishlist = (e) => {
    e.preventDefault();
    addToWishlist(product);
    toast({
      title: 'Added to wishlist',
      description: `${product.name} has been added to your wishlist.`
    });
  };

  const handleAddToCompare = (e) => {
    e.preventDefault();
    addToCompare(product);
    toast({
      title: 'Added to compare',
      description: `${product.name} has been added to compare list.`
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star key={i} size={14} className={i <= rating ? 'text-yellow-500' : 'text-gray-300'} />
      );
    }
    return stars;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group relative">
      {/* Badges */}
      <div className="absolute top-2 left-2 z-10 flex flex-col space-y-1">
        {product.badges && product.badges.includes('sale') && (
          <span className="bg-red-600 text-white text-xs px-2 py-1 rounded font-semibold">SALE</span>
        )}
        {product.badges && product.badges.includes('hot') && (
          <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded font-semibold">HOT</span>
        )}
        {product.badges && product.badges.includes('new') && (
          <span className="bg-green-500 text-white text-xs px-2 py-1 rounded font-semibold">NEW</span>
        )}
        {fits === true && (
          <span className="bg-gray-900 text-white text-xs px-2 py-1 rounded font-semibold">FITS YOUR CAR</span>
        )}
      </div>

      {/* Quick Actions */}
      <div className="absolute top-2 right-2 z-10 flex flex-col space-y-2 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button
          onClick={handleAddToWishlist}
          className="bg-white p-2 rounded-full shadow hover:bg-red-600 hover:text-white transition"
          title="Add to Wishlist"
        >
          <Heart size={16} strokeWidth={isInWishlist(product.id) ? 3 : 1.75} />
        </button>
        <button
          onClick={handleAddToCompare}
          className="bg-white p-2 rounded-full shadow hover:bg-red-600 hover:text-white transition"
          title="Add to Compare"
        >
          <Eye size={16} />
        </button>
      </div>

      {/* Image */}
      <Link to={`/product/${product.slug}`} className="block">
        <div className="aspect-square overflow-hidden bg-gray-50">
          <img
            src={product.image || '/placeholder.jpg'}
            alt={product.name}
            loading="lazy"
            decoding="async"
            width="400"
            height="400"
            onError={(e) => {
              e.target.src = '/placeholder.jpg';
            }}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      </Link>

      {/* Content */}
      <div className="p-4">
        <div className="text-xs text-gray-500 mb-1">SKU: {product.sku}</div>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-semibold text-gray-900 mb-2 hover:text-red-600 transition line-clamp-2 h-12">
            {product.name}
          </h3>
        </Link>

        {chips.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5 text-[11px] text-gray-600">
            {chips.map((c, i) => <span key={c.key} className={`rounded bg-gray-100 px-1.5 py-0.5 ${i === 0 ? 'font-semibold text-gray-800' : ''}`}>{c.value}{c.unit ? ` ${c.unit}` : ''}</span>)}
          </div>
        )}

        {/* Rating */}
        {product.rating > 0 ? (
          <div className="flex items-center mb-2">
            <div className="flex text-sm">{renderStars(Math.round(product.rating))}</div>
            <span className="text-xs text-gray-500 ml-2">({product.reviews})</span>
          </div>
        ) : (
          <div className="mb-2 h-[21px] text-xs text-gray-400">No reviews yet</div>
        )}

        {/* Price */}
        <div className="mb-3">
          <div className="flex items-center flex-wrap gap-x-2">
            <span className="text-lg font-bold text-gray-900">${product.price.toFixed(2)}</span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-sm text-gray-400 line-through">
                ${product.originalPrice.toFixed(2)}
              </span>
            )}
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-xs font-semibold text-green-600">
                {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
              </span>
            )}
          </div>
          {/* {product.promoActive && (
            <span className="text-xs text-red-600 font-medium">Promo Price</span>
          )} */}
          {/* {!product.promoActive && product.salePrice > 0 && product.price <= product.salePrice && product.originalPrice && product.originalPrice > product.price && (
            <span className="text-xs text-orange-600 font-medium">Sale Price</span>
          )} */}
          {!product.promoActive && product.isQuantityBreak && product.quantityBreakPriceDetails && (
            <span className="text-xs text-blue-600 font-medium">Qty Break Pricing Available</span>
          )}
        </div>

        {/* Stock Status */}
        <div className="mb-3">
          {product.stock > 0 ? (
            <span className="text-xs text-green-600 font-medium">In Stock ({product.stock})</span>
          ) : (
            <span className="text-xs text-red-600 font-medium">Out of Stock</span>
          )}
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={product.stock !== undefined && product.stock !== null && product.stock <= 0}
          className="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          <ShoppingCart size={18} />
          <span>Add to Cart</span>
        </button>
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
